/**
 * PATH: packages/bills/src/components/PdfInlineFieldEditor.tsx
 * FILE_ID: BILLS-PKG-CMP-PDFINLINEEDIT-001
 * PURPOSE: The in-place field editor that floats OVER a field's highlight
 *          box on the rendered PDF page — click a highlighted value on the
 *          document, type the correction, Enter → the sidebar + DB follow.
 *
 * WHY: Picking can only re-COPY text that is already printed on the page, so
 *      "edit the value from the PDF" was literally impossible — every click
 *      replaced the whole field with the clicked run and the operator undid
 *      it seconds later (2026-07-24 audit trail; "this edit function still
 *      does not work"). This editor is the actual edit affordance: it opens
 *      pre-filled (current value on click, swept text on drag) so nothing
 *      commits silently.
 *
 * KEYS: Enter commits (Shift+Enter = newline in multi-line descriptions),
 *       Esc cancels, blur commits (autosave + ⌘Z make that safe).
 *
 * RELATED:
 *   - PdfCanvasViewer.tsx mounts this over the target region's bbox.
 *   - BookkeepingWorkspace.tsx owns the editing state + commit routing
 *     (commit → the same captured→draft→autosave pipeline as picks).
 */

import { useEffect, useRef, useState } from 'react';

/**
 * "Amended value" patch — paints an edited field's LIVE value over its
 * printed location on the page, because the rendered page is a raster of
 * the original invoice and cannot itself change (2026-07-24 "its not
 * getting updated in the pdf"). White paper background covers the old
 * text; the value is typeset to read like the document (2026-07-25 "the
 * font in the pdf gets broken or changed on edit" — the first cut used the
 * UI font plus a dotted underline that scanned as strikethrough).
 */
export function PdfFieldPatch({
  field,
  rect,
  rows,
  value,
  pageHeight,
  alignRight = false,
  active = false,
}: {
  /** Field key — doubles as ``data-region-field`` for sidebar→PDF scroll. */
  field: string;
  /** Normalised (0..1) union bbox of the field's row boxes on this page. */
  rect: { x0: number; y0: number; x1: number; y1: number };
  /** How many printed rows the bbox unions — sizes the font to one row. */
  rows: number;
  value: string;
  /** Rendered page height in CSS px — converts row height into a font size. */
  pageHeight: number;
  /** Numeric column cells are right-aligned on the page — match them. */
  alignRight?: boolean;
  /**
   * Selected in the sidebar. The patch frames ITSELF (its rendered height,
   * which follows the amended text's own wrap points) — per-row region
   * boxes cut the patch mid-line and ringed covered-but-empty rows
   * ("bounded box got broken", 2026-07-25).
   */
  active?: boolean;
}) {
  const rowH = ((rect.y1 - rect.y0) * pageHeight) / Math.max(rows, 1);
  const fontSize = Math.max(7, rowH * 0.74);
  return (
    <div
      data-region-field={field}
      className="pointer-events-none absolute z-[9] bg-white"
      title="Edited value (the original text is covered)"
      style={{
        left: `${rect.x0 * 100}%`,
        top: `${rect.y0 * 100}%`,
        width: `${(rect.x1 - rect.x0) * 100}%`,
        minHeight: `${(rect.y1 - rect.y0) * 100}%`,
        // White halo swallows the antialiased fringes of the covered text.
        boxShadow: '0 0 0 3px #fff',
        outline: active ? '1px solid hsl(var(--primary) / 0.8)' : 'none',
        outlineOffset: 1,
        // Typeset like the paper, not like the app: invoices are almost
        // always Helvetica/Arial-family, near-black ink, plain text.
        fontFamily: 'Helvetica, Arial, sans-serif',
        color: '#1f2937',
        fontSize,
        lineHeight: `${rowH}px`,
        textAlign: alignRight ? 'right' : 'left',
      }}
    >
      {value}
    </div>
  );
}

export function PdfInlineFieldEditor({
  rect,
  value,
  multiline,
  onCommit,
  onCancel,
}: {
  /** Normalised (0..1) union bbox of the field's highlight on this page. */
  rect: { x0: number; y0: number; x1: number; y1: number };
  /** Prefill — the field's current value, or the drag-swept text. */
  value: string;
  /** Descriptions get a textarea (wrapped lines); numbers/dates an input. */
  multiline: boolean;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(value);
  const doneRef = useRef(false);
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    fieldRef.current?.focus();
    fieldRef.current?.select();
  }, []);

  const finish = (commit: boolean, v: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (commit) onCommit(v);
    else onCancel();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      finish(false, text);
    } else if (e.key === 'Enter' && !(multiline && e.shiftKey)) {
      e.preventDefault();
      finish(true, text);
    }
  };

  const shared = {
    value: text,
    onKeyDown,
    onBlur: () => finish(true, text),
    // The pick overlay + drag-pan both listen for mousedown on ancestors —
    // typing/selecting inside the editor must never start a pick or a pan.
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    className:
      'w-full resize-none border-2 border-primary bg-background px-1.5 py-0.5 text-xs ' +
      'text-foreground shadow-lg outline-none placeholder:text-muted-foreground',
  } as const;

  return (
    <div
      className="absolute z-30"
      style={{
        left: `${rect.x0 * 100}%`,
        top: `${rect.y0 * 100}%`,
        width: `${Math.max(rect.x1 - rect.x0, 0.12) * 100}%`,
        minWidth: '9rem',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {multiline ? (
        <textarea
          ref={(el) => {
            fieldRef.current = el;
          }}
          rows={Math.max(2, Math.min(6, text.split('\n').length + 1))}
          onChange={(e) => setText(e.target.value)}
          aria-label="Edit field value"
          {...shared}
        />
      ) : (
        <input
          ref={(el) => {
            fieldRef.current = el;
          }}
          type="text"
          onChange={(e) => setText(e.target.value)}
          aria-label="Edit field value"
          {...shared}
        />
      )}
      <p className="mt-0.5 w-max border border-border bg-background/95 px-1 py-px text-[10px] leading-tight text-muted-foreground shadow-sm">
        Enter to apply · Esc to cancel
      </p>
    </div>
  );
}
